import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { resolveCommerceImageUrl } from '@/lib/media';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fase 8 (docs/COMMERCE_THEME_EDITOR_DESIGN.md §10): one reusable
// image+text block instead of near-duplicate "campagne"/"donatie"/
// "organisatieverhaal" types -- same renderer for storefront and editor
// preview, matching the pattern established for Hero since fase 3.
export const ImageTextSection = ({ section = {}, theme = {} }) => {
  const settings = section.settings || {};
  const { title, body, primary_cta: primaryCta, image, image_position: imagePosition = 'right' } = settings;
  const imageUrl = resolveCommerceImageUrl(image);
  const primaryColor = theme.colors?.primary || '#0F766E';

  return (
    <section className={`grid gap-8 items-center ${imageUrl ? 'sm:grid-cols-2' : ''}`}>
      {imageUrl && (
        <div className={`rounded-2xl overflow-hidden aspect-[4/3] ${imagePosition === 'left' ? 'sm:order-1' : 'sm:order-2'}`}>
          <img src={imageUrl} alt={image?.alt_text || ''} className="w-full h-full object-cover" />
        </div>
      )}
      <div className={`space-y-3 ${imageUrl ? (imagePosition === 'left' ? 'sm:order-2' : 'sm:order-1') : ''}`}>
        {title && <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>}
        {body && <p className="text-slate-600 whitespace-pre-line">{body}</p>}
        {primaryCta?.text && primaryCta?.link && (
          <a href={primaryCta.link} className="inline-block">
            <Button className="rounded-xl text-white hover:opacity-90" style={{ backgroundColor: primaryColor }}>
              {primaryCta.text}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        )}
      </div>
    </section>
  );
};

let faqItemIdCounter = 0;

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  const [panelId] = useState(() => `faq-answer-${++faqItemIdCounter}`);
  return (
    <div className="border-b border-slate-200 py-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 text-left"
        aria-expanded={open}
        aria-controls={panelId}
      >
        <span className="font-medium text-slate-900">{question}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p id={panelId} className="mt-2 text-sm text-slate-600 whitespace-pre-line">{answer}</p>}
    </div>
  );
};

export const FaqSection = ({ section = {} }) => {
  const settings = section.settings || {};
  const { title, items = [] } = settings;
  if (items.length === 0) return null; // nothing configured -- render nothing rather than an empty shell
  return (
    <section className="space-y-4">
      {title && <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>}
      <div>
        {items.map((item, index) => (
          <FaqItem key={index} question={item.question} answer={item.answer} />
        ))}
      </div>
    </section>
  );
};

// orgId is omitted in the editor's live preview on purpose -- previewing
// this section shouldn't let an admin accidentally subscribe themselves;
// the button is simply inert there (same non-interactive-preview pattern
// as Hero's onCtaClick).
export const NewsletterSection = ({ section = {}, orgId, theme = {} }) => {
  const settings = section.settings || {};
  const { title, subtitle, button_text: buttonText } = settings;
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const primaryColor = theme.colors?.primary || '#0F766E';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orgId || !email) return;
    try {
      setSubmitting(true);
      await axios.post(`${API}/public/commerce/${orgId}/newsletter/subscribe`, { email });
      toast.success('Bedankt voor je aanmelding!');
      setEmail('');
    } catch (error) {
      toast.error('Aanmelden mislukt, probeer het later opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-2xl bg-slate-50 p-8 text-center space-y-4">
      {title && <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>}
      {subtitle && <p className="text-slate-600">{subtitle}</p>}
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
        <Input
          type="email"
          required
          aria-label="E-mailadres"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jouw@email.nl"
          className="flex-1"
        />
        <Button type="submit" disabled={submitting || !orgId} className="rounded-xl shrink-0 text-white hover:opacity-90" style={{ backgroundColor: primaryColor }}>
          {buttonText || 'Aanmelden'}
        </Button>
      </form>
    </section>
  );
};
